import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';

import { createActionsSlice } from '@/shared/store/actions/actionsSlice.ts';
import { createAssetsSlice } from '@/shared/store/assets/assetsSlice.ts';
import { createAuthStatusSlice } from '@/shared/store/auth/authSlice.ts';
import { createModalSlice } from '@/shared/store/modal/modalSlice.ts';
import { createPopupSlice } from '@/shared/store/popup/popupSlice.ts';
import { createTypesOperationsSlice } from '@/shared/store/types-operations/types-operationsSlice.ts';
import { createUISlice } from '@/shared/store/ui/uiSlice.ts';
import { LS_KEYS } from '@/shared/utils/constants.ts';

import { ActionsStoreSliceProps } from '@/shared/store/actions/actionsSlice.types.ts';
import { AssetsSliceProps } from '@/shared/store/assets/assetsSlice.types.ts';
import { AuthStatusSliceProps } from '@/shared/store/auth/authSlice.types.ts';
import { ModalSliceProps } from '@/shared/store/modal/modalSlice.types.ts';
import { PopupSliceProps } from '@/shared/store/popup/popupSlice.types.ts';
import { TypesOperationsSliceProps } from '@/shared/store/types-operations/types-operations.types.ts';
import { UiSliceProps } from '@/shared/store/ui/uiSlice.types.ts';

export interface AppStoreProps
  extends ActionsStoreSliceProps,
    AuthStatusSliceProps,
    AssetsSliceProps,
    UiSliceProps,
    ModalSliceProps,
    PopupSliceProps,
    TypesOperationsSliceProps {}

export const useGlobalStore = create<AppStoreProps>()(
  devtools(
    persist(
      (...store) => ({
        ...createActionsSlice(...store),
        ...createAuthStatusSlice(...store),
        ...createAssetsSlice(...store),
        ...createUISlice(...store),
        ...createModalSlice(...store),
        ...createPopupSlice(...store),
        ...createTypesOperationsSlice(...store),
      }),
      {
        name: LS_KEYS.UI_STORE,
        partialize: store => ({
          /**
           * This is a list of all the keys that we want to persist in the local storage.
           * */
          accountName: store.accountName,
          confirmSaveSiteHours: store.confirmSaveSiteHours,
          currency: store.currency,
          currentLanguage: store.currentLanguage,
          displayTimeZone: store.displayTimeZone,
          distance: store.distance,
          endTime: store.endTime,
          floor: store.floor,
          floorsList: store.floorsList,
          isCellCopyTipActive: store.isCellCopyTipActive,
          isShowInactive: store.isShowInactive,
          mapFilter: store.mapFilter,
          mapTrackersWithOpenPopup: store.mapTrackersWithOpenPopup,
          site: store.site,
          siteCalendars: store.siteCalendars,
          sitePolicies: store.sitePolicies,
          sitesList: store.sitesList,
          sitesListLoading: store.sitesListLoading,
          siteTimeZone: store.siteTimeZone,
          startTime: store.startTime,
          systemView: store.systemView,
          tagsId: store.tagsId,
          timeFormat: store.timeFormat,
          userSettings: store.userSettings,
        }),
        version: 1,
      },
    ),
    { name: 'Global Store' },
  ),
);
